import { authSessionGeneration, hasExplicitLogout } from "../../utils/authStorage";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import notificationService from "../../services/notificationService.js";

// ─────────────────────────────────────────────
// Async thunks
// ─────────────────────────────────────────────

const offlineNotifications = (getState) => getState()?.offline?.isOnline === false ||
  (typeof navigator !== "undefined" && navigator.onLine === false);
const OFFLINE_ACTION_MESSAGE = "Notifications are read-only offline. Connect to the internet to change them.";

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Record<string, unknown> | void, {}>} */
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (params = {}, { rejectWithValue, getState }) => {
    if (offlineNotifications(getState)) return rejectWithValue(OFFLINE_ACTION_MESSAGE);
    const generation = authSessionGeneration();
    try {
      const data = await notificationService.getNotifications(params);
      if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue("Session changed");
      return data.data; // { notifications, pagination, unreadCount }
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const markNotificationRead = createAsyncThunk(
  "notifications/markAsRead",
  async (id, { rejectWithValue, getState }) => {
    if (offlineNotifications(getState)) return rejectWithValue(OFFLINE_ACTION_MESSAGE);
    try {
      await notificationService.markAsRead(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to mark notification as read"
      );
    }
  }
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const markAllNotificationsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_, { rejectWithValue, getState }) => {
    if (offlineNotifications(getState)) return rejectWithValue(OFFLINE_ACTION_MESSAGE);
    try {
      await notificationService.markAllAsRead();
      return true;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to mark all as read"
      );
    }
  }
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async (id, { rejectWithValue, getState }) => {
    if (offlineNotifications(getState)) return rejectWithValue(OFFLINE_ACTION_MESSAGE);
    try {
      await notificationService.deleteNotification(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to delete notification"
      );
    }
  }
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const deleteAllNotifications = createAsyncThunk(
  "notifications/deleteAllNotifications",
  async (_, { rejectWithValue, getState }) => {
    if (offlineNotifications(getState)) return rejectWithValue(OFFLINE_ACTION_MESSAGE);
    try {
      await notificationService.deleteAllNotifications();
      return true;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to delete all notifications"
      );
    }
  }
);

// ─────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────

const initialState = {
  notifications: [],
  unreadCount: 0,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  },
  loading: false,
  error: null,
  // Tracks the last time notifications were fetched so components
  // can decide whether to refresh.
  lastFetched: null,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    restoreNotifications(state, action) {
      // Hydrating a cached snapshot must not change an active network request.
      Object.assign(state, action.payload);
    },
    clearNotifications() {
      return initialState;
    },
    clearNotificationError(state) {
      state.error = null;
    },
    // Optimistically decrement unread count (used by dropdown on open)
    resetUnreadCount(state) {
      state.unreadCount = 0;
    },
  },
  extraReducers: (builder) => {
    // ── fetchNotifications ──
    builder
      .addCase("auth/logoutUser/pending", () => initialState)
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload?.notifications || [];
        state.unreadCount = action.payload?.unreadCount ?? 0;
        state.pagination = action.payload?.pagination || initialState.pagination;
        state.lastFetched = Date.now();
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── markNotificationRead ──
    builder
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const id = action.payload;
        const notification = state.notifications.find((n) => n._id === id);
        if (notification && !notification.read) {
          notification.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });

    // ── markAllNotificationsRead ──
    builder
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications.forEach((n) => {
          n.read = true;
        });
        state.unreadCount = 0;
      });

    // ── deleteNotification ──
    builder
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const id = action.payload;
        const idx = state.notifications.findIndex((n) => n._id === id);
        if (idx !== -1) {
          if (!state.notifications[idx].read) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
          state.notifications.splice(idx, 1);
          state.pagination.total = Math.max(0, state.pagination.total - 1);
        }
      });

    // ── deleteAllNotifications ──
    builder
      .addCase(deleteAllNotifications.fulfilled, (state) => {
        state.notifications = [];
        state.unreadCount = 0;
        state.pagination = initialState.pagination;
      });
  },
});

export const { clearNotifications, restoreNotifications, clearNotificationError, resetUnreadCount } = notificationSlice.actions;

// ─────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────

export const selectNotifications = (state) => state.notifications.notifications;
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectNotificationsLoading = (state) => state.notifications.loading;
export const selectNotificationsError = (state) => state.notifications.error;
export const selectNotificationsPagination = (state) => state.notifications.pagination;
export const selectLastFetched = (state) => state.notifications.lastFetched;

export default notificationSlice.reducer;
